import { useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import useIsMobile from "../hooks/useIsMobile";
import useSheetData from "../hooks/useSheetData";
import Reveal from "../components/common/Reveal";
import SectionTag from "../components/common/SectionTag";
import Icon from "../components/common/Icon";
import Seo from "../components/common/Seo";
import { SheetLoading, SheetEmpty } from "../components/common/SheetState";
import { colors, fonts, emphasis, btnOutline, sectionPad, maxW, springHover, rowShift, arrowSlide, accentAt } from "../styles/theme";
import slugify from "../utils/slugify";
import { getLinkMeta } from "../utils/portfolioLink";

export default function PortfolioPage() {
  const mob = useIsMobile();
  const navigate = useNavigate();
  const [filter, setFilter] = useState("All");
  const { data: rows, status } = useSheetData("Portfolio");

  const PORTFOLIO = useMemo(() => rows.map((p, i) => ({ ...p, color: accentAt(i) })), [rows]);
  const types = ["All", ...Array.from(new Set(PORTFOLIO.map((p) => p.type)))];
  const filtered = filter === "All" ? PORTFOLIO : PORTFOLIO.filter((p) => p.type === filter);

  const openProject = (p) => {
    navigate(`/portfolio/${p.slug || slugify(p.title)}`);
  };

  return (
    <div style={{ background: colors.paper, minHeight: "100vh", paddingTop: 74 }}>
      <Seo
        title="Portfolio | Charith"
        description="Case studies and projects delivered by Charith across web, cloud, e-commerce and mobile."
        path="/portfolio"
      />
      <section style={{ background: colors.paper, ...sectionPad(mob) }}>
        <Reveal style={{ ...maxW, maxWidth: 720 }}>
          <SectionTag>Our Work</SectionTag>
          <h1 style={{ fontFamily: fonts.display, fontWeight: 600, fontSize: mob ? 32 : "clamp(30px,4.2vw,54px)", lineHeight: 1.12, color: colors.ink, letterSpacing: "-.01em" }}>Projects that <span style={emphasis}>speak</span></h1>
        </Reveal>
      </section>

      {status === "loading" ? (
        <section style={{ background: colors.paper, padding: mob ? "0 6% 90px" : "0 6% 140px", textAlign: "center" }}>
          <SheetLoading label="Loading portfolio…" />
        </section>
      ) : status === "error" ? (
        <section style={{ background: colors.paper, padding: mob ? "0 6% 90px" : "0 6% 140px" }}>
          <SheetEmpty icon="Briefcase" title="Couldn't load the portfolio" message="We're unable to reach our project list right now — please check back shortly." />
        </section>
      ) : PORTFOLIO.length === 0 ? (
        <section style={{ background: colors.paper, padding: mob ? "0 6% 90px" : "0 6% 140px" }}>
          <Reveal style={{ ...maxW, textAlign: "center", border: `1px solid ${colors.border}`, borderRadius: 6, padding: mob ? "50px 24px" : "80px 40px" }}>
            <Icon name="Briefcase" size={30} style={{ color: colors.rust, marginBottom: 20 }} />
            <h2 style={{ fontFamily: fonts.display, fontWeight: 600, fontSize: mob ? 24 : 30, color: colors.ink, marginBottom: 12 }}>Our work is coming soon</h2>
            <p style={{ color: colors.inkFaint, fontFamily: fonts.body, fontSize: mob ? 14.5 : 15.5, lineHeight: 1.75, maxWidth: 440, margin: "0 auto 30px" }}>
              We're putting together case studies from recent client and internship projects. Check back shortly — or get in touch if you'd like to be one of the first.
            </p>
            <Link to="/contact" style={{ ...btnOutline, textDecoration: "none", display: "inline-block" }}>Start a Project</Link>
          </Reveal>
        </section>
      ) : (
        <section style={{ background: colors.paper, padding: mob ? "0 6% 56px" : "0 6% 96px" }}>
          <div style={{ ...maxW }}>
            <div style={{ display: "flex", gap: mob ? 16 : 26, marginBottom: 42, flexWrap: "wrap", borderBottom: `1px solid ${colors.border}`, paddingBottom: 18 }}>
              {types.map((t) => (
                <button
                  key={t}
                  onClick={() => setFilter(t)}
                  style={{
                    background: "none", border: "none", cursor: "pointer", padding: 0,
                    color: filter === t ? colors.rust : colors.inkFaint,
                    fontFamily: fonts.body, fontSize: 14, fontWeight: filter === t ? 700 : 500,
                  }}
                >
                  {t}
                </button>
              ))}
            </div>

            <div style={{ borderTop: `1px solid ${colors.borderStrong}` }}>
              <AnimatePresence mode="popLayout">
                {filtered.map((p, i) => (
                  <motion.div
                    key={p.slug || p.title}
                    layout
                    initial="offscreen"
                    animate="onscreen"
                    exit={{ opacity: 0 }}
                    whileHover="hover"
                    variants={{ offscreen: { opacity: 0, y: 12 }, onscreen: { opacity: 1, y: 0 }, ...rowShift }}
                    transition={{ duration: 0.3 }}
                    onClick={() => openProject(p)}
                    style={{ display: "grid", gridTemplateColumns: mob ? "1fr" : "50px 1fr 220px auto", alignItems: mob ? "start" : "center", gap: mob ? 8 : 24, padding: mob ? "20px 4px" : "26px 20px", borderBottom: `1px solid ${colors.border}`, cursor: "pointer" }}
                  >
                    {!mob && <span style={{ fontFamily: fonts.display, fontStyle: "italic", fontSize: 18, color: colors.inkFainter }}>{String(i + 1).padStart(2, "0")}</span>}
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6, flexWrap: "wrap" }}>
                        <h3 style={{ fontFamily: fonts.body, fontWeight: 700, fontSize: mob ? 16 : 18, color: colors.ink }}>{p.title}</h3>
                        <span style={{ border: `1px solid ${p.color}66`, color: p.color, fontFamily: fonts.body, fontSize: 10.5, fontWeight: 700, padding: "2px 8px", borderRadius: 20 }}>{p.type}</span>
                        {p.playstoreurl && (() => {
                          const link = getLinkMeta(p.type);
                          return (
                            <a
                              href={p.playstoreurl}
                              target="_blank"
                              rel="noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              style={{ display: "inline-flex", alignItems: "center", gap: 4, color: colors.inkFaint, fontFamily: fonts.body, fontSize: 11.5, fontWeight: 600, textDecoration: "none" }}
                            >
                              {link.shortLabel} <Icon name={link.icon} size={11} />
                            </a>
                          );
                        })()}
                      </div>
                      <p style={{ color: colors.inkFaint, fontFamily: fonts.body, fontSize: 13.5, lineHeight: 1.6, maxWidth: 460 }}>{p.desc}</p>
                    </div>
                    <span style={{ color: colors.inkFainter, fontFamily: fonts.body, fontSize: 12.5 }}>{p.tag}</span>
                    {!mob && <motion.span variants={arrowSlide} transition={springHover} style={{ display: "flex" }}><Icon name="ArrowUpRight" size={16} style={{ color: colors.inkFaint }} /></motion.span>}
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
